import React from 'react';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbSegment {
  label: string;
  path?: string;
  isActive?: boolean;
}

interface BreadcrumbBarProps {
  segments: BreadcrumbSegment[];
  onNavigate?: (path: string) => void;
}

/**
 * BreadcrumbBar — Shows navigation path: Group > Case > Stage > Panel
 *
 * Derived from current route + React context.
 */
export default function BreadcrumbBar({ segments, onNavigate }: BreadcrumbBarProps) {
  const handleClick = (segment: BreadcrumbSegment) => {
    if (segment.path && onNavigate && !segment.isActive) {
      onNavigate(segment.path);
    }
  };

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
      {/* Home icon */}
      {segments.length > 0 && (
        <button
          onClick={() => onNavigate?.('/dashboard')}
          className="
            p-1 rounded-md
            text-[var(--color-text-muted)]
            hover:text-[var(--color-text-secondary)]
            hover:bg-[var(--color-bg-hover)]
            transition-colors
          "
          title="Go to dashboard"
        >
          <Home className="w-4 h-4" />
        </button>
      )}

      {segments.length > 0 && (
        <ChevronRight className="w-4 h-4 text-[var(--color-text-dim)]" />
      )}

      {/* Segments */}
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;

        return (
          <React.Fragment key={index}>
            <button
              onClick={() => handleClick(segment)}
              disabled={!segment.path || segment.isActive}
              className={`
                px-2 py-1 rounded-md transition-colors
                ${segment.isActive || isLast
                  ? 'text-[var(--color-text-primary)] font-medium cursor-default'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }
                ${segment.path && !segment.isActive ? 'cursor-pointer' : ''}
              `}
            >
              {segment.label}
            </button>

            {!isLast && (
              <ChevronRight className="w-4 h-4 text-[var(--color-text-dim)]" />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
